import { get, isEmpty } from 'lodash';
import { checkValue, appendMcicid, dateHelper } from 'src/components/common/helper';
import { useLocalization } from '@shared-ui/localization-context';

export const getLocalizationData = (data, label, dValue, device) => {
  return device === 'DESKTOP' ? get(data, label, dValue) : get(data, `mobile[${label}]`, dValue);
};

export const getWizardUrlOverrideURL = (lob, url, wizardUrlOverride) => {
  const overrideDefaults = ['returnDefault', 'departureDefault', 'returnPreset', 'departurePreset'];
  let overrideURL = url;
  if (checkValue(wizardUrlOverride, true)) {
    const objURL = wizardUrlOverride.filter((item) => item?.wizardTab === lob);
    if (checkValue(objURL?.[0]) && checkValue(objURL?.[0].searchUrlOverride)) {
      overrideURL = objURL[0].searchUrlOverride;
    }
  }

  // Sometimes override is getting as
  // ex: /go/hotel/search/Destination/?CityName=Wales{returnDefault=Wales}
  // then remove {} unwanted part from the override if the word after the { is one of overrideController
  if (overrideURL?.indexOf('{') > -1 && overrideURL?.indexOf('}')) {
    const getOverrideURL = overrideURL?.split('{')?.[0];
    const lastPart = overrideURL?.split('{')?.[1];
    const isContainOverrideDefaults = overrideDefaults.some((overrideDefault) => lastPart.includes(overrideDefault))
    if (isContainOverrideDefaults) {
      overrideURL = checkValue(getOverrideURL) ? getOverrideURL : url;
    }
  }
  if (!overrideURL.includes('?')) {
    overrideURL = overrideURL + '?';
  } else {
    overrideURL = overrideURL + '&';
  }

  return overrideURL;
};

export const formatDateWithLang = (locale, date, format?) => {
  let dateFormats = 'dd/MM/yyyy';
  if (checkValue(format)) {
    dateFormats = format;
  } else {
    if (locale === 'en_US') {
      dateFormats = 'MM/dd/yyyy';
    }
  }

  return { date, dateFormats };
};

export const getDateFormatWithLang = (locale, format?) => {
  let dateFormats = 'dd/MM/yyyy';
  if (checkValue(format)) {
    dateFormats = format;
  } else {
    if (locale === 'en_US') {
      dateFormats = 'MM/dd/yyyy';
    }
  }

  return dateFormats;
};

export const getWizOverrideDateParaName = (lob, selectDate, overridePara, defaultPara, wizardUrlOverride) => {
  let returnPara: any = defaultPara;
  if (checkValue(wizardUrlOverride, true)) {
    const objURL = wizardUrlOverride.filter((item) => item.wizardTab === lob);
    if (checkValue(objURL, true)) {
      if (checkValue(objURL[0])) {
        if (checkValue(objURL[0].searchUrlOverride)) {
          if (checkValue(objURL[0][selectDate])) {
            returnPara = overridePara;
          }
        }
      }
    }
  }
  return returnPara;
};

export const linkTabOpenner = (url, mccidData) => {
  const mcicidUrl: string = appendMcicid(
    url,
    mccidData?.templateType,
    mccidData?.campaignUrl,
    mccidData?.mainUrl,
    mccidData?.referrerId,
    mccidData?.extraParam
  );
  const a = document.createElement('a');
  a.target = '_blank';
  a.href = mcicidUrl;
  a.click();
};

interface TypeheadErrorsProps {
  error: string | boolean | undefined;
  typeheadId: string;
}
export const renderInvalidTypeheadStyles = (errors: Array<TypeheadErrorsProps>) => {
  errors.forEach(({ error, typeheadId }) => {
    const el = document.querySelector(`#${typeheadId}-menu`); //menu typeheades
    const el2 = document.querySelector(`[data-stid=${typeheadId}] > div:nth-child(2)`); //dialog typeheades

    //for menu typeheads
    if (el) {
      if (error !== '' && error !== false && error !== undefined) {
        el.classList.add('is-invalid');
      } else {
        el.classList.remove('is-invalid');
      }
    }

    //for dialog typeheads
    if (el2) {
      if (error !== '' && error !== false && error !== undefined) {
        el2.classList.add('is-invalid');
      } else {
        el2.classList.remove('is-invalid');
      }
    }
  });
};

export interface WizardDefaultProps {
  data: any;
  lob: 'Hotel' | 'Flight' | 'Car' | 'Package' | 'Activities' | 'Cruises';
  overrideController: 'returnDefault' | 'departureDefault' | 'returnPreset' | 'departurePreset';
}
export const getWizardDefaults = (props: WizardDefaultProps) => {
  const { data, lob, overrideController } = props;
  const lobWizardOverrides = get(data, 'wizardUrlOverride', '');
  const wizardDefaults = [];

  if (checkValue(lobWizardOverrides)) {
    const getLobWizard = getWizardsOverrides(data, lob);
    const getLob = getLobWizard?.[0];
    if (
      checkValue(getLob) &&
      checkValue(getLob?.searchUrlOverride) &&
      getLob?.searchUrlOverride?.indexOf('{') > -1 &&
      getLob?.searchUrlOverride?.indexOf('}') > -1
    ) {
      // an array to collect the strings that are found
      // check the default is coming with the param
      // ex:  '/go/hotel/search/Destination/?CityName=Wales{returnDefault=Wales}{departureDefault=landon}'
      // return [{returnDefault = Wales},{departureDefault=landon}  ]
      const rxp = /{([^}]+)}/g;
      const strToCheck = getLob?.searchUrlOverride; // '/go/hotel/search/Destination/?CityName=Wales{returnDefault=Wales}';
      let curMatch;

      // eslint-disable-next-line no-cond-assign
      while ((curMatch = rxp.exec(strToCheck))) {
        const getWizardDefault = curMatch?.[1];
        let getWizardDefaultValue = {};
        if (getWizardDefault?.toLowerCase()?.indexOf(overrideController?.toLowerCase()) > -1) {
          getWizardDefaultValue = {
            key: `${overrideController}`,
            value: getWizardDefault.replace(`${overrideController}=`, ''),
          };
        }

        if (!isEmpty(getWizardDefaultValue)) {
          wizardDefaults.push(getWizardDefaultValue);
        }
      }
    }
  }

  return wizardDefaults;
};

/**
 *
 * @param data API DATA object
 * @param lob  lob
 * @returns
 */
export const getWizardsOverrides = (data: any, lob: 'Hotel' | 'Flight' | 'Car' | 'Package' | 'Activities' | 'Cruises') => {
  const lobWizardOverrides = get(data, 'wizardUrlOverride', '');
  let getLobWizard = undefined;
  if (checkValue(lobWizardOverrides)) {
    getLobWizard = lobWizardOverrides?.filter((item) => item?.wizardTab === lob);
  }
  return getLobWizard;
};

/**
 *
 * @param dateFromWeeks - CMS setting departureDate/returnDate it set from 1 weeks from today
 * @param DateFixed - CMS setting fixed date departureDateFixed/ returnDateFixed
 * @param wizardOverridesDate - CMS Wizard Override Date
 * @param manualDate - manual date set from our side 2 weeks form today
 * @returns
 */
export const wizardDate = (
  dateFromWeeks,
  DateFixed,
  wizardOverridesDate,
  manualDate,
  daysInFuture,
  lengthOfStay,
  searchedDateFormat = 'yyyy-MM-dd'
) => {
  const { formatDateString } = useLocalization();
  let date = manualDate;

  try {
    // Check Date From Weeks
    if (checkValue(dateFromWeeks)) {
      const getDateFromWeeks = new Date(dateFromWeeks);
      if (getDateFromWeeks >= new Date()) {
        date = dateFromWeeks;
      }
    }

    // Check DaysInFuture
    if (checkValue(daysInFuture) && !isNaN(daysInFuture) && !checkValue(lengthOfStay)) {
      const getDaysFuture = dateHelper.addDate(Number(daysInFuture));
      if (getDaysFuture >= new Date()) {
        date = getDaysFuture;
      }
    }

    // Check DaysInFuture + Stays
    if (checkValue(daysInFuture) && !isNaN(daysInFuture) && checkValue(lengthOfStay) && !isNaN(lengthOfStay)) {
      const getDaysInFutureAndStays = dateHelper.addDate(Number(daysInFuture) + Number(lengthOfStay));
      if (getDaysInFutureAndStays >= new Date()) {
        date = getDaysInFutureAndStays;
      }
    }

    // Check Date Fixed
    if (checkValue(DateFixed)) {
      const getDateFixed = new Date(DateFixed);
      if (getDateFixed >= new Date()) {
        date = getDateFixed;
      }
    }

    // Check Wizards Overrides Date
    if (checkValue(wizardOverridesDate)) {
      const getWizardOverridesDate = new Date(wizardOverridesDate);
      if (getWizardOverridesDate >= new Date()) {
        date = getWizardOverridesDate;
      }
    }
  } catch (e) {
    date = manualDate;
  }
  
  return formatDateString(new Date(date), { raw: searchedDateFormat });
};

/**
 * Return Date Need to future date than the departureDate
 * @param departureDate
 * @param returnDate
 * @returns
 */
export const getValidReturnDate = (departureDate, returnDate, searchedDateFormat = 'yyyy-MM-dd') => {
  const { formatDateString } = useLocalization();
  let getReturnDate;
  try {
    const dDate = new Date(departureDate);
    const rDate = new Date(returnDate);

    if (dDate >= rDate) {
      dDate.setDate(dDate.getDate() + 1);

      // Convert to UTC
      const utcYear = dDate.getUTCFullYear();
      const utcMonth = ('0' + (dDate.getUTCMonth() + 1)).slice(-2); // Adding 1 because month is zero-based
      const utcDay = ('0' + dDate.getUTCDate()).slice(-2);

      // Format the date in yyyy-mm-dd format
      const formattedUTCDate = utcYear + '-' + utcMonth + '-' + utcDay;

      getReturnDate = formattedUTCDate;
      
    } else {

      // Convert to UTC
      const utcYear = rDate.getUTCFullYear();
      const utcMonth = ('0' + (rDate.getUTCMonth() + 1)).slice(-2); // Adding 1 because month is zero-based
      const utcDay = ('0' + rDate.getUTCDate()).slice(-2);

      // Format the date in yyyy-mm-dd format
      const formattedUTCDate = utcYear + '-' + utcMonth + '-' + utcDay;

      getReturnDate = formattedUTCDate;

    }
  } catch (e) {
    const today = new Date();
    today.setDate(today.getDate() + 30);
    getReturnDate = today;
  }

  return formatDateString(getReturnDate, { raw: searchedDateFormat });
};
